import { useDispatch, useSelector } from "react-redux";
import { useState } from "react";
import { MDBTable, MDBTableBody, MDBTableHead } from "mdb-react-ui-kit";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import {
  fetchLeaderboardWinners,
  runLeaderboardPayoutJob,
  acceptLeaderboardWinner,
  rejectLeaderboardWinner,
  markWinnerPaid,
} from "../../actions/referralActions";
import useHasPermission from "../../hooks/useHasPermission";
import { PERMISSIONS } from "../../constants/permissions";
import {
  downloadBtnStyle,
  downloadCSV,
  getDynamicKeys,
  toTitleCase,
  modalOverlay,
  modalBox,
  modalHeader,
  modalBody,
  closeBtn,
} from "./shared";

const LeaderboardWinners = () => {
  const dispatch = useDispatch();
  const canApprove = useHasPermission(PERMISSIONS.APPROVE_REFERRAL_REWARDS);
  const canReject = useHasPermission(PERMISSIONS.REJECT_REFERRAL_REWARDS);
  const canRunJob = useHasPermission(PERMISSIONS.RUN_LEADERBOARD_PAYOUT);

  const [winnersPage, setWinnersPage] = useState(1);
  const [filterReferrerId, setFilterReferrerId] = useState("");
  const [filterStatusId, setFilterStatusId] = useState("");
  const [filterIsPaid, setFilterIsPaid] = useState("");
  const [filterPeriodStart, setFilterPeriodStart] = useState("");

  const [runModal, setRunModal] = useState(false);
  const [runPeriodStart, setRunPeriodStart] = useState("");
  const [runLoading, setRunLoading] = useState(false);

  const [rejectModal, setRejectModal] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const [acceptModal, setAcceptModal] = useState(false);
  const [acceptingId, setAcceptingId] = useState(null);
  const [acceptForm, setAcceptForm] = useState({ payout_amount: "", payout_currency: "NGN", payout_reference: "" });

  const { loading, data, error } = useSelector((state) => state.getLeaderboardWinners) || {};
  const winners = data?.data;
  const dynamicKeys = getDynamicKeys(winners?.items || []);
  const actionCols = (canApprove || canReject || canRunJob) ? 1 : 0;
  const totalCols = Math.max(dynamicKeys.length, 1) + actionCols;

  const handleFilter = () => {
    setWinnersPage(1);
    dispatch(fetchLeaderboardWinners(
      1, 100,
      filterReferrerId || null,
      filterStatusId || null,
      filterIsPaid !== "" ? filterIsPaid === "true" : null,
      filterPeriodStart || null,
    ));
  };

  const handlePageChange = (page) => {
    setWinnersPage(page);
    dispatch(fetchLeaderboardWinners(
      page, 100,
      filterReferrerId || null,
      filterStatusId || null,
      filterIsPaid !== "" ? filterIsPaid === "true" : null,
      filterPeriodStart || null,
    ));
  };

  const handleDownload = () => {
    if (!winners?.items?.length) {
      toast.warning("No data to download");
      return;
    }
    downloadCSV(
      "leaderboard_winners",
      dynamicKeys.map(toTitleCase),
      winners.items.map((item) => dynamicKeys.map((k) => item[k] ?? "")),
    );
  };

  const handleRunJob = async (e) => {
    e.preventDefault();
    setRunLoading(true);
    try {
      await dispatch(runLeaderboardPayoutJob(runPeriodStart || null));
      toast.success("Leaderboard payout job started");
      setRunModal(false);
      setRunPeriodStart("");
      document.body.style.overflow = "auto";
      dispatch(fetchLeaderboardWinners(winnersPage, 100));
    } catch (err) {
      toast.error(err?.message || "Failed to run job");
    } finally {
      setRunLoading(false);
    }
  };

  const openAcceptModal = (id) => {
    setAcceptingId(id);
    setAcceptForm({ payout_amount: "", payout_currency: "NGN", payout_reference: "" });
    setAcceptModal(true);
    document.body.style.overflow = "hidden";
  };

  const closeAcceptModal = () => {
    setAcceptModal(false);
    setAcceptingId(null);
    document.body.style.overflow = "auto";
  };

  const handleAcceptSubmit = async (e) => {
    e.preventDefault();
    if (!acceptForm.payout_amount) { toast.error("Payout amount is required"); return; }
    setActionLoading(true);
    try {
      await dispatch(acceptLeaderboardWinner(acceptingId, {
        payout_amount: Number(acceptForm.payout_amount),
        payout_currency: acceptForm.payout_currency.trim(),
        payout_reference: acceptForm.payout_reference.trim() || undefined,
      }));
      toast.success("Winner accepted");
      closeAcceptModal();
      dispatch(fetchLeaderboardWinners(winnersPage, 100));
    } catch (err) {
      toast.error(err?.message || "Failed to accept");
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectModal = (id) => {
    setRejectingId(id);
    setRejectReason("");
    setRejectModal(true);
    document.body.style.overflow = "hidden";
  };

  const closeRejectModal = () => {
    setRejectModal(false);
    setRejectingId(null);
    setRejectReason("");
    document.body.style.overflow = "auto";
  };

  const handleRejectSubmit = async (e) => {
    e.preventDefault();
    if (!rejectReason.trim()) { toast.error("Reason is required"); return; }
    setActionLoading(true);
    try {
      await dispatch(rejectLeaderboardWinner(rejectingId, rejectReason.trim()));
      toast.success("Winner rejected");
      closeRejectModal();
      dispatch(fetchLeaderboardWinners(winnersPage, 100));
    } catch (err) {
      toast.error(err?.message || "Failed to reject");
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkPaid = async (id) => {
    setActionLoading(true);
    try {
      await dispatch(markWinnerPaid(id));
      toast.success("Winner marked as paid");
      dispatch(fetchLeaderboardWinners(winnersPage, 100));
    } catch (err) {
      toast.error(err?.message || "Failed to mark paid");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <Loader />;
  if (error) return <p className="text-danger text-center">{error}</p>;

  return (
    <>
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "16px", alignItems: "flex-end" }}>
        <div>
          <label style={{ display: "block", fontSize: "12px", color: "#aaa", marginBottom: "4px" }}>Referrer ID</label>
          <input value={filterReferrerId} onChange={(e) => setFilterReferrerId(e.target.value)} placeholder="Referrer ID" style={{ padding: "8px 12px", border: "1px solid #444", borderRadius: "6px", background: "#1a1a1a", color: "#fff", fontSize: "13px", width: "150px" }} />
        </div>
        <div>
          <label style={{ display: "block", fontSize: "12px", color: "#aaa", marginBottom: "4px" }}>Status ID</label>
          <input value={filterStatusId} onChange={(e) => setFilterStatusId(e.target.value)} placeholder="Status ID" style={{ padding: "8px 12px", border: "1px solid #444", borderRadius: "6px", background: "#1a1a1a", color: "#fff", fontSize: "13px", width: "120px" }} />
        </div>
        <div>
          <label style={{ display: "block", fontSize: "12px", color: "#aaa", marginBottom: "4px" }}>Paid</label>
          <select value={filterIsPaid} onChange={(e) => setFilterIsPaid(e.target.value)} style={{ padding: "8px 12px", border: "1px solid #444", borderRadius: "6px", background: "#1a1a1a", color: "#fff", fontSize: "13px" }}>
            <option value="">All</option>
            <option value="true">Paid</option>
            <option value="false">Unpaid</option>
          </select>
        </div>
        <div>
          <label style={{ display: "block", fontSize: "12px", color: "#aaa", marginBottom: "4px" }}>Period Start</label>
          <input type="date" value={filterPeriodStart} onChange={(e) => setFilterPeriodStart(e.target.value)} style={{ padding: "8px 12px", border: "1px solid #444", borderRadius: "6px", background: "#1a1a1a", color: "#fff", fontSize: "13px" }} />
        </div>
        <button onClick={handleFilter} style={{ padding: "8px 18px", background: "#007bff", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "600", fontSize: "13px" }}>
          Filter
        </button>
        {canRunJob && (
          <button onClick={() => { setRunModal(true); document.body.style.overflow = "hidden"; }} style={{ padding: "8px 18px", background: "#6f42c1", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "600", fontSize: "13px" }}>
            ▶ Run Payout Job
          </button>
        )}
        <button onClick={handleDownload} style={{ ...downloadBtnStyle, marginLeft: "auto" }}>
          ⬇ Download CSV
        </button>
      </div>

      <MDBTable responsive style={{ width: "100%", fontSize: "14px" }}>
        <MDBTableHead>
          <tr>
            {dynamicKeys.length > 0 ? (
              dynamicKeys.map((key) => <th key={key}>{toTitleCase(key)}</th>)
            ) : (
              <th>No Data</th>
            )}
            {actionCols > 0 && <th>Actions</th>}
          </tr>
        </MDBTableHead>
        <MDBTableBody>
          {winners?.items?.length > 0 ? (
            winners.items.map((item, idx) => (
              <tr key={item.id || idx}>
                {dynamicKeys.map((key) => (
                  <td key={key}>{item[key] != null ? String(item[key]) : "-"}</td>
                ))}
                {actionCols > 0 && (
                  <td>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                      {canApprove && (
                        <button onClick={() => openAcceptModal(item.id)} disabled={actionLoading} style={{ padding: "4px 10px", background: "#28a745", color: "#fff", border: "none", borderRadius: "4px", cursor: actionLoading ? "not-allowed" : "pointer", fontSize: "12px", fontWeight: "600" }}>
                          Accept
                        </button>
                      )}
                      {canReject && (
                        <button onClick={() => openRejectModal(item.id)} disabled={actionLoading} style={{ padding: "4px 10px", background: "#dc3545", color: "#fff", border: "none", borderRadius: "4px", cursor: actionLoading ? "not-allowed" : "pointer", fontSize: "12px", fontWeight: "600" }}>
                          Reject
                        </button>
                      )}
                      {canApprove && (
                        <button onClick={() => handleMarkPaid(item.id)} disabled={actionLoading} style={{ padding: "4px 10px", background: "#007bff", color: "#fff", border: "none", borderRadius: "4px", cursor: actionLoading ? "not-allowed" : "pointer", fontSize: "12px", fontWeight: "600" }}>
                          Mark Paid
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={totalCols} style={{ textAlign: "center", padding: "20px" }}>No leaderboard winners found</td>
            </tr>
          )}
        </MDBTableBody>
      </MDBTable>

      {winners?.page?.total_page > 1 && (
        <div style={{ display: "flex", gap: "8px", justifyContent: "center", marginTop: "16px" }}>
          {Array.from({ length: winners.page.total_page }, (_, i) => i + 1).map((page) => (
            <button key={page} onClick={() => handlePageChange(page)} style={{ padding: "6px 12px", border: "1px solid #444", borderRadius: "4px", background: winnersPage === page ? "#007bff" : "#1a1a1a", color: "#fff", cursor: "pointer" }}>
              {page}
            </button>
          ))}
        </div>
      )}

      {/* Run Payout Job Modal */}
      {runModal && (
        <div style={modalOverlay} onClick={() => { setRunModal(false); document.body.style.overflow = "auto"; }}>
          <div style={{ ...modalBox, maxWidth: "400px" }} className="referral-modal-box" onClick={(e) => e.stopPropagation()}>
            <div style={modalHeader} className="referral-modal-header">
              <h3 style={{ margin: 0, fontSize: "18px" }}>Run Leaderboard Payout Job</h3>
              <button style={closeBtn} onClick={() => { setRunModal(false); document.body.style.overflow = "auto"; }}>✕</button>
            </div>
            <div style={modalBody} className="referral-modal-body">
              <form onSubmit={handleRunJob}>
                <div style={{ marginBottom: "16px" }}>
                  <label style={{ display: "block", fontSize: "12px", color: "#aaa", marginBottom: "6px", fontWeight: "500" }}>Period Start (optional)</label>
                  <input type="date" value={runPeriodStart} onChange={(e) => setRunPeriodStart(e.target.value)} style={{ width: "100%", padding: "10px 12px", border: "1px solid #444", borderRadius: "6px", background: "#1a1a1a", color: "#fff", fontSize: "14px" }} />
                  <p style={{ margin: "8px 0 0", fontSize: "12px", color: "#aaa" }}>Leave empty to use the current period.</p>
                </div>
                <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                  <button type="button" onClick={() => { setRunModal(false); document.body.style.overflow = "auto"; }} style={{ padding: "10px 20px", border: "1px solid #444", borderRadius: "6px", background: "transparent", color: "#fff", cursor: "pointer" }}>
                    Cancel
                  </button>
                  <button type="submit" disabled={runLoading} style={{ padding: "10px 20px", background: "#6f42c1", color: "#fff", border: "none", borderRadius: "6px", cursor: runLoading ? "not-allowed" : "pointer", fontWeight: "600", opacity: runLoading ? 0.7 : 1 }}>
                    {runLoading ? "Running..." : "Run Job"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Accept Modal */}
      {acceptModal && (
        <div style={modalOverlay} onClick={closeAcceptModal}>
          <div style={{ ...modalBox, maxWidth: "440px" }} className="referral-modal-box" onClick={(e) => e.stopPropagation()}>
            <div style={modalHeader} className="referral-modal-header">
              <h3 style={{ margin: 0, fontSize: "18px" }}>Accept Winner</h3>
              <button style={closeBtn} onClick={closeAcceptModal}>✕</button>
            </div>
            <div style={modalBody} className="referral-modal-body">
              <form onSubmit={handleAcceptSubmit}>
                <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", color: "#aaa", marginBottom: "4px", fontWeight: "500" }}>Payout Amount *</label>
                    <input type="number" value={acceptForm.payout_amount} onChange={(e) => setAcceptForm((p) => ({ ...p, payout_amount: e.target.value }))} style={{ width: "100%", padding: "10px 12px", border: "1px solid #444", borderRadius: "6px", background: "#1a1a1a", color: "#fff", fontSize: "14px" }} min="0" step="0.01" required />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", color: "#aaa", marginBottom: "4px", fontWeight: "500" }}>Payout Currency</label>
                    <input value={acceptForm.payout_currency} onChange={(e) => setAcceptForm((p) => ({ ...p, payout_currency: e.target.value }))} style={{ width: "100%", padding: "10px 12px", border: "1px solid #444", borderRadius: "6px", background: "#1a1a1a", color: "#fff", fontSize: "14px" }} maxLength={10} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", color: "#aaa", marginBottom: "4px", fontWeight: "500" }}>Payout Reference</label>
                    <input value={acceptForm.payout_reference} onChange={(e) => setAcceptForm((p) => ({ ...p, payout_reference: e.target.value }))} style={{ width: "100%", padding: "10px 12px", border: "1px solid #444", borderRadius: "6px", background: "#1a1a1a", color: "#fff", fontSize: "14px" }} placeholder="Optional" />
                  </div>
                  <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                    <button type="button" onClick={closeAcceptModal} style={{ padding: "10px 20px", border: "1px solid #444", borderRadius: "6px", background: "transparent", color: "#fff", cursor: "pointer" }}>
                      Cancel
                    </button>
                    <button type="submit" disabled={actionLoading} style={{ padding: "10px 20px", background: "#28a745", color: "#fff", border: "none", borderRadius: "6px", cursor: actionLoading ? "not-allowed" : "pointer", fontWeight: "600", opacity: actionLoading ? 0.7 : 1 }}>
                      {actionLoading ? "Accepting..." : "Accept"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div style={modalOverlay} onClick={closeRejectModal}>
          <div style={{ ...modalBox, maxWidth: "420px" }} className="referral-modal-box" onClick={(e) => e.stopPropagation()}>
            <div style={modalHeader} className="referral-modal-header">
              <h3 style={{ margin: 0, fontSize: "18px" }}>Reject Winner</h3>
              <button style={closeBtn} onClick={closeRejectModal}>✕</button>
            </div>
            <div style={modalBody} className="referral-modal-body">
              <form onSubmit={handleRejectSubmit}>
                <div style={{ marginBottom: "16px" }}>
                  <label style={{ display: "block", fontSize: "12px", color: "#aaa", marginBottom: "6px", fontWeight: "500" }}>Reason *</label>
                  <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Provide a reason for rejection" style={{ width: "100%", padding: "10px 12px", border: "1px solid #444", borderRadius: "6px", background: "#1a1a1a", color: "#fff", fontSize: "14px", minHeight: "100px", resize: "vertical" }} required />
                </div>
                <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                  <button type="button" onClick={closeRejectModal} style={{ padding: "10px 20px", border: "1px solid #444", borderRadius: "6px", background: "transparent", color: "#fff", cursor: "pointer" }}>
                    Cancel
                  </button>
                  <button type="submit" disabled={actionLoading} style={{ padding: "10px 20px", background: "#dc3545", color: "#fff", border: "none", borderRadius: "6px", cursor: actionLoading ? "not-allowed" : "pointer", fontWeight: "600", opacity: actionLoading ? 0.7 : 1 }}>
                    {actionLoading ? "Rejecting..." : "Reject"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LeaderboardWinners;
